import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Edit3,
  Trash2,
  Save,
  X,
  Tag,
  Calendar,
  Store,
  Loader2,
  ImageOff,
  Plus,
  Minus,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ShoppingCart,
  Check,
  ListPlus,
  TrendingUp,
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { receiptsApi, tagsApi } from '../api/receipts';
import { shoppingListsApi } from '../api/shoppingLists';
import { toast } from '../components/ui/Toast';
import type { Receipt, ReceiptItem, Tag as TagType, ShoppingList } from '../types';
import { toFixed, formatEUR } from '../utils/format';

// =============================================
// Tag Picker Component
// =============================================
interface TagPickerProps {
  receiptId: string;
  currentTags: TagType[];
  allTags: TagType[];
  onTagAttached: () => void;
  onTagDetached: () => void;
}

const TagPicker: React.FC<TagPickerProps> = ({
  receiptId,
  currentTags,
  allTags,
  onTagAttached,
  onTagDetached,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const attachMutation = useMutation({
    mutationFn: (tagId: string) => tagsApi.attachToReceipt(receiptId, tagId),
    onSuccess: () => {
      onTagAttached();
      setInputValue('');
      setDropdownOpen(false);
    },
    onError: () => {
      toast.error('Fehler', 'Tag konnte nicht hinzugefügt werden.');
    },
  });

  const detachMutation = useMutation({
    mutationFn: (tagId: string) => tagsApi.detachFromReceipt(receiptId, tagId),
    onSuccess: () => {
      onTagDetached();
    },
    onError: () => {
      toast.error('Fehler', 'Tag konnte nicht entfernt werden.');
    },
  });

  const createAndAttachMutation = useMutation({
    mutationFn: async (name: string) => {
      const newTag = await tagsApi.create(name);
      await tagsApi.attachToReceipt(receiptId, newTag.id);
      return newTag;
    },
    onSuccess: () => {
      onTagAttached();
      setInputValue('');
      setDropdownOpen(false);
      toast.success('Tag erstellt', 'Neuer Tag wurde erstellt und hinzugefügt.');
    },
    onError: () => {
      toast.error('Fehler', 'Tag konnte nicht erstellt werden.');
    },
  });

  const currentTagIds = new Set(currentTags.map((t) => t.id));
  const filteredTags = allTags.filter(
    (t) =>
      !currentTagIds.has(t.id) &&
      t.name.toLowerCase().includes(inputValue.toLowerCase())
  );

  const exactMatch = allTags.find(
    (t) => t.name.toLowerCase() === inputValue.toLowerCase()
  );
  const showCreateOption =
    inputValue.trim().length > 0 && !exactMatch;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isLoading =
    attachMutation.isPending ||
    detachMutation.isPending ||
    createAndAttachMutation.isPending;

  return (
    <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-indigo-500/10">
      <p className="text-xs text-neutral-400 dark:text-neutral-500 uppercase tracking-wider flex items-center gap-1 mb-2">
        <Tag className="w-3 h-3" /> Tags
      </p>

      {/* Current tags as removable pills */}
      <div className="flex flex-wrap gap-2 mb-3">
        {currentTags.map((tag) => (
          <span
            key={tag.id}
            className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium
                       bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-300"
          >
            {tag.name}
            <button
              onClick={() => detachMutation.mutate(tag.id)}
              disabled={isLoading}
              className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-primary-200 dark:hover:bg-primary-500/30
                         transition-colors disabled:opacity-50"
              title={`Tag "${tag.name}" entfernen`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {currentTags.length === 0 && (
          <span className="text-xs text-neutral-400 dark:text-neutral-500 italic">
            Keine Tags vorhanden
          </span>
        )}
      </div>

      {/* Tag Input + Dropdown */}
      <div className="relative">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setDropdownOpen(true);
              }}
              onFocus={() => setDropdownOpen(true)}
              placeholder="Tag suchen oder neu erstellen..."
              className="w-full text-sm px-3 py-2 pr-8 border border-neutral-300 dark:border-indigo-500/20 rounded-lg
                         bg-white dark:bg-[#1e1e2e] dark:text-white placeholder:text-neutral-400
                         focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
            />
            <ChevronDown
              className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none"
            />
          </div>
        </div>

        {/* Dropdown */}
        {dropdownOpen && (filteredTags.length > 0 || showCreateOption) && (
          <div
            ref={dropdownRef}
            className="absolute top-full left-0 right-0 mt-1 z-10 bg-white dark:bg-[#1e1e2e]
                       border border-neutral-200 dark:border-indigo-500/20 rounded-lg shadow-lg
                       max-h-48 overflow-y-auto"
          >
            {filteredTags.map((tag) => (
              <button
                key={tag.id}
                onClick={() => attachMutation.mutate(tag.id)}
                disabled={isLoading}
                className="w-full text-left px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200
                           hover:bg-neutral-50 dark:hover:bg-white/5 transition-colors
                           flex items-center gap-2 disabled:opacity-50"
              >
                <Tag className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                {tag.name}
              </button>
            ))}
            {showCreateOption && (
              <button
                onClick={() => createAndAttachMutation.mutate(inputValue.trim())}
                disabled={isLoading}
                className="w-full text-left px-3 py-2 text-sm text-primary-600 dark:text-primary-400
                           hover:bg-primary-50 dark:hover:bg-primary-500/10 transition-colors
                           flex items-center gap-2 border-t border-neutral-100 dark:border-indigo-500/10
                           disabled:opacity-50"
              >
                <Plus className="w-3.5 h-3.5 shrink-0" />
                <span>
                  Neuen Tag <strong>"{inputValue.trim()}"</strong> erstellen
                </span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================
// Receipt Detail Page
// =============================================
export const ReceiptDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = useState(() => searchParams.get('edit') === 'true');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [editedItems, setEditedItems] = useState<ReceiptItem[]>([]);
  const [editedStoreName, setEditedStoreName] = useState('');
  const [editedDate, setEditedDate] = useState('');
  const [editedAmount, setEditedAmount] = useState('');

  // Shopping List Modal State
  const [showShoppingModal, setShowShoppingModal] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [singleAddItem, setSingleAddItem] = useState<ReceiptItem | null>(null);
  const [selectedListId, setSelectedListId] = useState<string>('');
  const [newListName, setNewListName] = useState('');
  const [isCreatingNewList, setIsCreatingNewList] = useState(false);

  const { data: receipt, isLoading } = useQuery<Receipt>({
    queryKey: ['receipt', id],
    queryFn: () => receiptsApi.get(id!),
    enabled: !!id,
  });

  const { data: allTags } = useQuery<TagType[]>({
    queryKey: ['tags'],
    queryFn: tagsApi.list,
    enabled: isEditing,
  });

  const [imageThumbUrls, setImageThumbUrls] = useState<(string | null)[]>([]);
  const [imageFullUrls, setImageFullUrls] = useState<(string | null)[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (receipt) {
      setEditedStoreName(receipt.store_name || receipt.store?.name || '');
      setEditedDate(receipt.receipt_date || '');
      setEditedAmount(receipt.total_amount?.toString() || '');
      setEditedItems(receipt.items || []);
    }
  }, [receipt]);

  // Load all receipt images (1 or several pieces of one long receipt).
  useEffect(() => {
    if (!receipt?.id) return;

    const imageCount = receipt.images && receipt.images.length > 0 ? receipt.images.length : 1;
    let cancelled = false;
    setImageLoading(true);
    setImageError(false);
    setImageThumbUrls(new Array(imageCount).fill(null));
    setImageFullUrls(new Array(imageCount).fill(null));
    setCurrentImageIndex(0);

    // First thumbnail decides imageLoading/imageError state — once it's there
    // the page renders, the rest stream in afterwards.
    receiptsApi.fetchImageBlob(receipt.id, 'thumb', 0).then((blob) => {
      if (cancelled) return;
      const url = URL.createObjectURL(blob);
      setImageThumbUrls((prev) => {
        const next = [...prev];
        next[0] = url;
        return next;
      });
      setImageLoading(false);
    }).catch(() => {
      if (!cancelled) {
        setImageLoading(false);
        setImageError(true);
      }
    });

    // First full image and all remaining thumbnails/originals load in parallel.
    receiptsApi.fetchImageBlob(receipt.id, 'original', 0).then((blob) => {
      if (cancelled) return;
      setImageFullUrls((prev) => {
        const next = [...prev];
        next[0] = URL.createObjectURL(blob);
        return next;
      });
    }).catch(() => { /* full image is best-effort */ });

    for (let i = 1; i < imageCount; i++) {
      const idx = i;
      receiptsApi.fetchImageBlob(receipt.id, 'thumb', idx).then((blob) => {
        if (cancelled) return;
        setImageThumbUrls((prev) => {
          const next = [...prev];
          next[idx] = URL.createObjectURL(blob);
          return next;
        });
      }).catch(() => { /* missing image stays null */ });
      receiptsApi.fetchImageBlob(receipt.id, 'original', idx).then((blob) => {
        if (cancelled) return;
        setImageFullUrls((prev) => {
          const next = [...prev];
          next[idx] = URL.createObjectURL(blob);
          return next;
        });
      }).catch(() => { /* full is best-effort */ });
    }

    return () => {
      cancelled = true;
      setImageThumbUrls((prev) => {
        prev.forEach((u) => { if (u) URL.revokeObjectURL(u); });
        return [];
      });
      setImageFullUrls((prev) => {
        prev.forEach((u) => { if (u) URL.revokeObjectURL(u); });
        return [];
      });
    };
  }, [receipt?.id, receipt?.images?.length]);

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof receiptsApi.update>[1]) =>
      receiptsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receipt', id] });
      setIsEditing(false);
      toast.success('Gespeichert', 'Änderungen wurden übernommen.');
    },
    onError: () => {
      toast.error('Fehler', 'Änderungen konnten nicht gespeichert werden.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => receiptsApi.delete(id!),
    onSuccess: () => {
      // Invalidate receipts list cache so it refreshes without the deleted item
      queryClient.invalidateQueries({ queryKey: ['receipts'] });
      toast.success('Gelöscht', 'Kassenzettel wurde entfernt.');
      navigate('/receipts');
    },
    onError: () => {
      toast.error('Fehler', 'Kassenzettel konnte nicht gelöscht werden.');
    },
  });

  // Shopping Lists
  const { data: shoppingLists, isLoading: shoppingListsLoading } = useQuery<ShoppingList[]>({
    queryKey: ['shoppingLists'],
    queryFn: shoppingListsApi.list,
    enabled: showShoppingModal,
  });

  const createListMutation = useMutation({
    mutationFn: (name: string) => shoppingListsApi.create({ name }),
    onSuccess: (newList) => {
      queryClient.invalidateQueries({ queryKey: ['shoppingLists'] });
      setSelectedListId(newList.id);
      setIsCreatingNewList(false);
      setNewListName('');
    },
    onError: () => {
      toast.error('Fehler', 'Liste konnte nicht erstellt werden.');
    },
  });

  const addToShoppingListMutation = useMutation({
    mutationFn: async ({ listId, items }: { listId: string; items: ReceiptItem[] }) => {
      await Promise.all(
        items.map((item) =>
          shoppingListsApi.addItem(listId, {
            name: item.name,
            quantity: String(item.quantity),
            note: item.category?.name || undefined,
          })
        )
      );
    },
    onSuccess: (_, { items }) => {
      queryClient.invalidateQueries({ queryKey: ['shoppingLists'] });
      const count = items.length;
      toast.success(
        'Zur Einkaufsliste hinzugefügt',
        count === 1
          ? `"${items[0].name}" wurde hinzugefügt.`
          : `${count} Artikel wurden hinzugefügt.`
      );
      setShowShoppingModal(false);
      setSelectedItemIds(new Set());
      setSingleAddItem(null);
      setSelectedListId('');
    },
    onError: () => {
      toast.error('Fehler', 'Artikel konnten nicht hinzugefügt werden.');
    },
  });

  const openBulkShoppingModal = () => {
    const items = receipt?.items || [];
    const allIds = new Set(items.map((i) => i.id));
    setSelectedItemIds(allIds);
    setSingleAddItem(null);
    setSelectedListId(shoppingLists?.[0]?.id || '');
    setIsCreatingNewList(false);
    setNewListName('');
    setShowShoppingModal(true);
  };

  const openSingleShoppingModal = (item: ReceiptItem) => {
    setSingleAddItem(item);
    setSelectedItemIds(new Set());
    setSelectedListId(shoppingLists?.[0]?.id || '');
    setIsCreatingNewList(false);
    setNewListName('');
    setShowShoppingModal(true);
  };

  const handleConfirmAddToList = async () => {
    let listId = selectedListId;

    if (isCreatingNewList) {
      if (!newListName.trim()) return;
      const newList = await createListMutation.mutateAsync(newListName.trim());
      listId = newList.id;
    }

    if (!listId) return;

    const allItems = receipt?.items || [];
    const itemsToAdd = singleAddItem
      ? [singleAddItem]
      : allItems.filter((i) => selectedItemIds.has(i.id));

    if (itemsToAdd.length === 0) return;

    addToShoppingListMutation.mutate({ listId, items: itemsToAdd });
  };

  const toggleItemSelection = (itemId: string) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const handleSave = () => {
    updateMutation.mutate({
      store_name: editedStoreName,
      receipt_date: editedDate || undefined,
      total_amount: editedAmount ? parseFloat(editedAmount) : undefined,
      items: editedItems.map((item) => ({
        name: item.name,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.total_price,
        category_id: item.category_id,
      })),
    });
  };

  const handleItemChange = (
    index: number,
    field: keyof ReceiptItem,
    value: string | number
  ) => {
    setEditedItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'quantity' || field === 'unit_price') {
        const qty = field === 'quantity' ? Number(value) : updated[index].quantity;
        const price = field === 'unit_price' ? Number(value) : updated[index].unit_price;
        updated[index].total_price = parseFloat((qty * price).toFixed(2));
      }
      return updated;
    });
  };

  const handleAddItem = () => {
    setEditedItems((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        receipt_id: id!,
        name: '',
        quantity: 1,
        unit_price: 0,
        total_price: 0,
        created_at: new Date().toISOString(),
      },
    ]);
  };

  const handleRemoveItem = (index: number) => {
    setEditedItems((prev) => prev.filter((_, i) => i !== index));
  };

  const invalidateReceipt = () => {
    queryClient.invalidateQueries({ queryKey: ['receipt', id] });
    queryClient.invalidateQueries({ queryKey: ['tags'] });
  };

  if (isLoading) {
    return <LoadingSpinner fullPage text="Beleg wird geladen..." />;
  }

  if (!receipt) {
    return (
      <div className="text-center py-20">
        <h2 className="text-lg font-semibold text-neutral-700 dark:text-neutral-200">
          Kassenzettel nicht gefunden
        </h2>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/receipts')}>
          Zurück zur Liste
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/receipts')}
            className="p-2 rounded-lg text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/10 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-neutral-800 dark:text-white truncate">
              {receipt.store?.name || receipt.store_name || 'Kassenzettel'}
            </h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              {receipt.receipt_date
                ? new Date(receipt.receipt_date).toLocaleDateString('de-DE', {
                    weekday: 'long',
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })
                : 'Kein Datum'}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button
                variant="ghost"
                className="flex-1 sm:flex-none"
                onClick={() => {
                  setIsEditing(false);
                  setEditedStoreName(receipt.store_name || receipt.store?.name || '');
                  setEditedDate(receipt.receipt_date || '');
                  setEditedAmount(receipt.total_amount?.toString() || '');
                  setEditedItems(receipt.items || []);
                }}
              >
                <X className="w-4 h-4 mr-1" />
                Abbrechen
              </Button>
              <Button className="flex-1 sm:flex-none" onClick={handleSave} loading={updateMutation.isPending}>
                <Save className="w-4 h-4 mr-1" />
                Speichern
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" className="flex-1 sm:flex-none" onClick={() => setIsEditing(true)}>
                <Edit3 className="w-4 h-4 mr-1" />
                Bearbeiten
              </Button>
              <Button variant="danger" className="flex-1 sm:flex-none" onClick={() => setShowDeleteModal(true)}>
                <Trash2 className="w-4 h-4 mr-1" />
                Löschen
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left: Image (gallery if multiple) */}
        <Card padding="sm" className="md:col-span-1">
          <div
            className="relative aspect-[3/4] bg-neutral-100 dark:bg-neutral-800 rounded-lg flex items-center justify-center overflow-hidden cursor-pointer"
            onClick={() => setShowImageModal(true)}
          >
            {imageLoading && (
              <div className="flex flex-col items-center text-neutral-400">
                <Loader2 className="w-8 h-8 animate-spin mb-2" />
                <span className="text-xs">Bild wird geladen...</span>
              </div>
            )}
            {!imageLoading && imageError && (
              <div className="flex flex-col items-center text-neutral-400">
                <ImageOff className="w-10 h-10 mb-2" />
                <span className="text-xs">Bild nicht verfügbar</span>
              </div>
            )}
            {!imageLoading && !imageError && imageThumbUrls[currentImageIndex] && (
              <img
                src={imageThumbUrls[currentImageIndex] || undefined}
                alt={`Kassenzettel Bild ${currentImageIndex + 1} von ${imageThumbUrls.length}`}
                className="w-full h-full object-contain"
              />
            )}

            {imageThumbUrls.length > 1 && (
              <>
                {currentImageIndex > 0 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((i) => Math.max(0, i - 1)); }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
                    aria-label="Vorheriges Bild"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                )}
                {currentImageIndex < imageThumbUrls.length - 1 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((i) => Math.min(imageThumbUrls.length - 1, i + 1)); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
                    aria-label="Nächstes Bild"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                )}
                <span className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-black/60 text-white text-[11px] font-semibold tabular-nums">
                  {currentImageIndex + 1}/{imageThumbUrls.length}
                </span>
              </>
            )}
          </div>

          {imageThumbUrls.length > 1 && (
            <div className="flex justify-center gap-1.5 mt-2">
              {imageThumbUrls.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentImageIndex
                      ? 'bg-primary-500 w-5'
                      : 'bg-neutral-300 dark:bg-neutral-600 hover:bg-neutral-400'
                  }`}
                  aria-label={`Bild ${idx + 1} anzeigen`}
                />
              ))}
            </div>
          )}

          <p className="text-xs text-center text-neutral-400 mt-2">
            Klicken zum Vergrößern
          </p>
        </Card>

        {/* Right: Details */}
        <div className="md:col-span-2 space-y-4">
          {/* Info Card */}
          <Card>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-neutral-400 dark:text-neutral-500 uppercase tracking-wider flex items-center gap-1 mb-1">
                  <Store className="w-3 h-3" /> Geschäft
                </p>
                {isEditing ? (
                  <Input
                    value={editedStoreName}
                    onChange={(e) => setEditedStoreName(e.target.value)}
                    placeholder="Geschäftsname"
                  />
                ) : (
                  <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                    {receipt.store?.name || receipt.store_name || '--'}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs text-neutral-400 dark:text-neutral-500 uppercase tracking-wider flex items-center gap-1 mb-1">
                  <Calendar className="w-3 h-3" /> Datum
                </p>
                {isEditing ? (
                  <Input
                    type="date"
                    value={editedDate}
                    onChange={(e) => setEditedDate(e.target.value)}
                  />
                ) : (
                  <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                    {receipt.receipt_date
                      ? new Date(receipt.receipt_date).toLocaleDateString('de-DE')
                      : '--'}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mb-1">
                  Gesamtbetrag
                </p>
                {isEditing ? (
                  <Input
                    type="number"
                    step="0.01"
                    value={editedAmount}
                    onChange={(e) => setEditedAmount(e.target.value)}
                    placeholder="0.00"
                  />
                ) : (
                  <p className="text-xl font-bold font-mono tabular-nums text-neutral-800 dark:text-neutral-100">
                    {receipt.total_amount != null
                      ? formatEUR(receipt.total_amount)
                      : '--,-- EUR'}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mb-1">Status</p>
                {(() => {
                  const statusMap: Record<string, { label: string; className: string }> = {
                    pending: { label: 'Wird verarbeitet...', className: 'bg-warning-50 text-warning-700' },
                    processing: { label: 'Wird erkannt...', className: 'bg-info-50 text-info-700' },
                    ocr_done: { label: 'Erkannt', className: 'bg-secondary-50 text-secondary-600' },
                    reviewed: { label: 'Geprüft', className: 'bg-primary-50 text-primary-600' },
                    completed: { label: 'Fertig', className: 'bg-success-50 text-success-700' },
                    error: { label: 'Fehler', className: 'bg-error-50 text-error-700' },
                  };
                  const cfg = statusMap[receipt.status] || { label: receipt.status, className: 'bg-neutral-100 text-neutral-600' };
                  return (
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${cfg.className}`}>
                      {cfg.label}
                    </span>
                  );
                })()}
              </div>
            </div>

            {/* Tags — read-only or interactive depending on edit mode */}
            {isEditing ? (
              <TagPicker
                receiptId={receipt.id}
                currentTags={receipt.tags || []}
                allTags={allTags || []}
                onTagAttached={invalidateReceipt}
                onTagDetached={invalidateReceipt}
              />
            ) : (
              receipt.tags && receipt.tags.length > 0 && (
                <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-indigo-500/10">
                  <p className="text-xs text-neutral-400 dark:text-neutral-500 uppercase tracking-wider flex items-center gap-1 mb-2">
                    <Tag className="w-3 h-3" /> Tags
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {receipt.tags.map((tag) => (
                      <span
                        key={tag.id}
                        className="px-2.5 py-1 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300"
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </div>
              )
            )}
          </Card>

          {/* Items Card */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                Positionen ({(isEditing ? editedItems : receipt.items || []).length})
              </h3>
              {!isEditing && (receipt.items || []).length > 0 && (
                <button
                  onClick={openBulkShoppingModal}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg
                             text-emerald-600 dark:text-emerald-400
                             bg-emerald-50 dark:bg-emerald-500/10
                             hover:bg-emerald-100 dark:hover:bg-emerald-500/20
                             transition-colors"
                >
                  <ListPlus className="w-3.5 h-3.5" />
                  Zur Einkaufsliste
                </button>
              )}
            </div>
            <div className="space-y-2">
              {(isEditing ? editedItems : receipt.items || []).map((item, index) => (
                <div
                  key={item.id || index}
                  className="flex items-center gap-3 py-2 px-3 rounded-lg bg-neutral-50 dark:bg-white/5"
                >
                  {isEditing ? (
                    <>
                      <button
                        onClick={() => handleRemoveItem(index)}
                        className="p-1 rounded text-error-400 hover:text-error-600 hover:bg-error-50 dark:hover:bg-error-500/10 transition-colors shrink-0"
                        title="Position entfernen"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                        placeholder="Artikelname"
                        className="flex-1 min-w-0 text-sm bg-white dark:bg-[#1e1e2e] border border-neutral-300 dark:border-indigo-500/20 rounded px-2 py-1
                                   dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                        className="w-14 text-sm text-center bg-white dark:bg-[#1e1e2e] border border-neutral-300 dark:border-indigo-500/20 rounded px-1 py-1
                                   dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                        step="0.001"
                      />
                      <span className="text-xs text-neutral-400">x</span>
                      <input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                        className="w-16 text-sm text-right bg-white dark:bg-[#1e1e2e] border border-neutral-300 dark:border-indigo-500/20 rounded px-1 py-1
                                   font-mono dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                        step="0.01"
                      />
                      <p className="w-16 text-sm font-mono font-medium text-right tabular-nums text-neutral-800 dark:text-neutral-100">
                        {toFixed(item.total_price, 2)}
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-neutral-700 dark:text-neutral-200 truncate">{item.name}</p>
                        <p className="text-xs text-neutral-400">
                          {item.quantity} x {toFixed(item.unit_price, 2)} EUR
                        </p>
                      </div>
                      <p className="text-sm font-mono font-medium tabular-nums text-neutral-800 dark:text-neutral-100 shrink-0">
                        {toFixed(item.total_price, 2)} EUR
                      </p>
                      <button
                        onClick={() => openSingleShoppingModal(item)}
                        className="p-1.5 rounded-lg text-neutral-400 hover:text-emerald-500 dark:hover:text-emerald-400
                                   hover:bg-emerald-50 dark:hover:bg-emerald-500/10 transition-colors shrink-0"
                        title={`"${item.name}" zur Einkaufsliste hinzufügen`}
                      >
                        <ShoppingCart className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/prices?q=${encodeURIComponent(item.name)}`)}
                        className="p-1.5 rounded-lg text-neutral-400 hover:text-primary-500 dark:hover:text-primary-400
                                   hover:bg-primary-50 dark:hover:bg-primary-500/10 transition-colors shrink-0"
                        title={`"${item.name}" im Preisvergleich nachschlagen`}
                      >
                        <TrendingUp className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              ))}
              {(receipt.items || []).length === 0 && !isEditing && (
                <p className="text-sm text-neutral-400 text-center py-4">
                  Keine Positionen vorhanden
                </p>
              )}
            </div>
            {isEditing && (
              <button
                onClick={handleAddItem}
                className="mt-3 flex items-center gap-2 w-full justify-center py-2.5 text-sm font-medium
                           text-primary-500 border border-dashed border-primary-300 dark:border-primary-500/30
                           rounded-lg hover:bg-primary-50 dark:hover:bg-primary-500/10 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Position hinzufügen
              </button>
            )}
          </Card>
        </div>
      </div>

      {/* Image Modal */}
      <Modal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        title={
          imageThumbUrls.length > 1
            ? `Kassenzettel-Bild ${currentImageIndex + 1} von ${imageThumbUrls.length}`
            : "Kassenzettel-Bild"
        }
        size="xl"
      >
        {(imageFullUrls[currentImageIndex] || imageThumbUrls[currentImageIndex]) ? (
          <div className="relative">
            <img
              src={imageFullUrls[currentImageIndex] || imageThumbUrls[currentImageIndex] || undefined}
              alt={`Kassenzettel Bild ${currentImageIndex + 1}`}
              className="w-full h-auto rounded-lg"
            />
            {imageThumbUrls.length > 1 && (
              <>
                {currentImageIndex > 0 && (
                  <button
                    type="button"
                    onClick={() => setCurrentImageIndex((i) => Math.max(0, i - 1))}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center"
                    aria-label="Vorheriges Bild"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                )}
                {currentImageIndex < imageThumbUrls.length - 1 && (
                  <button
                    type="button"
                    onClick={() => setCurrentImageIndex((i) => Math.min(imageThumbUrls.length - 1, i + 1))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center"
                    aria-label="Nächstes Bild"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                )}
                <div className="flex justify-center gap-1.5 mt-3">
                  {imageThumbUrls.map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        idx === currentImageIndex
                          ? 'bg-primary-500 w-5'
                          : 'bg-neutral-300 dark:bg-neutral-600'
                      }`}
                      aria-label={`Bild ${idx + 1} anzeigen`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-neutral-400">
            {imageLoading ? (
              <>
                <Loader2 className="w-10 h-10 animate-spin mb-3" />
                <span className="text-sm">Bild wird geladen...</span>
              </>
            ) : (
              <>
                <ImageOff className="w-10 h-10 mb-3" />
                <span className="text-sm">Bild nicht verfügbar</span>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Kassenzettel löschen?"
        size="sm"
      >
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">
          Möchtest du diesen Kassenzettel wirklich löschen? Diese Aktion kann nicht
          rückgängig gemacht werden.
        </p>
        <div className="flex gap-3 justify-end">
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
            Abbrechen
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteMutation.mutate()}
            loading={deleteMutation.isPending}
          >
            Endgültig löschen
          </Button>
        </div>
      </Modal>

      {/* Shopping List Modal */}
      <Modal
        isOpen={showShoppingModal}
        onClose={() => {
          setShowShoppingModal(false);
          setSingleAddItem(null);
          setSelectedItemIds(new Set());
          setIsCreatingNewList(false);
          setNewListName('');
        }}
        title="Zur Einkaufsliste hinzufügen"
        size="sm"
      >
        <div className="space-y-4">
          {/* Artikel-Vorschau */}
          {singleAddItem ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-neutral-50 dark:bg-white/5">
              <ShoppingCart className="w-4 h-4 text-emerald-500 shrink-0" />
              <span className="text-sm text-neutral-700 dark:text-neutral-200 truncate">
                {singleAddItem.name}
              </span>
            </div>
          ) : (
            <div className="space-y-1">
              <p className="text-xs text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mb-2">
                Artikel auswählen
              </p>
              <div className="max-h-44 overflow-y-auto space-y-1 pr-1">
                {(receipt?.items || []).map((item) => (
                  <label
                    key={item.id}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer
                               hover:bg-neutral-50 dark:hover:bg-white/5 transition-colors"
                  >
                    <div
                      className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                        selectedItemIds.has(item.id)
                          ? 'bg-emerald-500 border-emerald-500'
                          : 'border-neutral-300 dark:border-neutral-600'
                      }`}
                      onClick={() => toggleItemSelection(item.id)}
                    >
                      {selectedItemIds.has(item.id) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span
                      className="text-sm text-neutral-700 dark:text-neutral-200 flex-1 truncate"
                      onClick={() => toggleItemSelection(item.id)}
                    >
                      {item.name}
                    </span>
                    <span className="text-xs text-neutral-400 shrink-0">
                      {item.quantity}x
                    </span>
                  </label>
                ))}
              </div>
              <div className="flex items-center justify-between pt-1">
                <span className="text-xs text-neutral-400">
                  {selectedItemIds.size} von {(receipt?.items || []).length} ausgewählt
                </span>
                <button
                  onClick={() => {
                    const allIds = new Set((receipt?.items || []).map((i) => i.id));
                    setSelectedItemIds(
                      selectedItemIds.size === (receipt?.items || []).length
                        ? new Set()
                        : allIds
                    );
                  }}
                  className="text-xs text-primary-500 hover:text-primary-600 transition-colors"
                >
                  {selectedItemIds.size === (receipt?.items || []).length
                    ? 'Alle abwählen'
                    : 'Alle auswählen'}
                </button>
              </div>
            </div>
          )}

          {/* Trennlinie */}
          <div className="border-t border-neutral-200 dark:border-indigo-500/10" />

          {/* Liste auswählen */}
          <div>
            <p className="text-xs text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mb-2">
              Einkaufsliste wählen
            </p>

            {shoppingListsLoading ? (
              <div className="flex items-center gap-2 text-sm text-neutral-400 py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Listen werden geladen...
              </div>
            ) : (
              <div className="space-y-1.5">
                {(shoppingLists || []).map((list) => (
                  <label
                    key={list.id}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer border transition-colors ${
                      selectedListId === list.id && !isCreatingNewList
                        ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/50'
                        : 'border-neutral-200 dark:border-indigo-500/15 hover:border-emerald-300 dark:hover:border-emerald-500/30'
                    }`}
                    onClick={() => {
                      setSelectedListId(list.id);
                      setIsCreatingNewList(false);
                    }}
                  >
                    <div
                      className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${
                        selectedListId === list.id && !isCreatingNewList
                          ? 'border-emerald-500 bg-emerald-500'
                          : 'border-neutral-300 dark:border-neutral-600'
                      }`}
                    >
                      {selectedListId === list.id && !isCreatingNewList && (
                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200 truncate">
                        {list.name}
                      </p>
                      <p className="text-xs text-neutral-400">
                        {list.item_count} Artikel
                      </p>
                    </div>
                  </label>
                ))}

                {/* Neue Liste erstellen */}
                <label
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer border transition-colors ${
                    isCreatingNewList
                      ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/50'
                      : 'border-dashed border-neutral-300 dark:border-indigo-500/20 hover:border-emerald-300 dark:hover:border-emerald-500/30'
                  }`}
                  onClick={() => {
                    setIsCreatingNewList(true);
                    setSelectedListId('');
                  }}
                >
                  <div
                    className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${
                      isCreatingNewList
                        ? 'border-emerald-500 bg-emerald-500'
                        : 'border-neutral-300 dark:border-neutral-600'
                    }`}
                  >
                    {isCreatingNewList && (
                      <div className="w-1.5 h-1.5 rounded-full bg-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    {isCreatingNewList ? (
                      <input
                        type="text"
                        value={newListName}
                        onChange={(e) => setNewListName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleConfirmAddToList()}
                        placeholder="Name der neuen Liste..."
                        autoFocus
                        className="w-full text-sm bg-transparent border-none outline-none
                                   text-neutral-700 dark:text-neutral-200 placeholder:text-neutral-400"
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <p className="text-sm font-medium text-primary-500 dark:text-primary-400 flex items-center gap-1.5">
                        <Plus className="w-3.5 h-3.5" />
                        Neue Liste erstellen
                      </p>
                    )}
                  </div>
                </label>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-1">
            <Button
              variant="ghost"
              className="flex-1"
              onClick={() => {
                setShowShoppingModal(false);
                setSingleAddItem(null);
                setSelectedItemIds(new Set());
                setIsCreatingNewList(false);
                setNewListName('');
              }}
            >
              Abbrechen
            </Button>
            <Button
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-700"
              onClick={handleConfirmAddToList}
              loading={addToShoppingListMutation.isPending || createListMutation.isPending}
              disabled={
                (!selectedListId && !isCreatingNewList) ||
                (isCreatingNewList && !newListName.trim()) ||
                (!singleAddItem && selectedItemIds.size === 0)
              }
            >
              <ShoppingCart className="w-4 h-4 mr-1" />
              Hinzufügen
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
